// ----------------------------------------------------------------------
// Pattern detectors for personal data inside free text.
//
// PHONE / EMAIL / ID are reversible: the gateway swaps them for placeholders
// and restores them later. SECRET is dropped for good — the model never needs
// a card number or a token, so there is nothing to restore.
//
// Every pattern refuses to start or end inside a longer token (`[\w-]`
// lookarounds), so UUID segments, dates and JSON numbers are never mistaken
// for phones.
// ----------------------------------------------------------------------

export type SensitiveKind = 'PHONE' | 'EMAIL' | 'ID' | 'SECRET';

export type SensitiveMatch = {
  start: number;
  end: number;
  kind: SensitiveKind;
  value: string;
};

const digitsOf = (value: string) => value.replace(/\D/g, '');

const passesLuhn = (value: string): boolean => {
  const digits = digitsOf(value);
  let sum = 0;
  for (let index = 0; index < digits.length; index += 1) {
    let digit = Number(digits[digits.length - 1 - index]);
    if (index % 2 === 1) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  return sum % 10 === 0;
};

const hasPhoneLength = (value: string) => {
  const { length } = digitsOf(value);
  return length >= 10 && length <= 15;
};

/** Mobile and landline operator codes of Uzbekistan. */
const UZ_CODES = '(?:20|33|50|55|61|62|65|66|67|69|7[0-9]|88|90|91|93|94|95|97|98|99)';

const PATTERNS: ReadonlyArray<{
  kind: SensitiveKind;
  regex: RegExp;
  accept?: (value: string) => boolean;
}> = [
  // Secrets first, so they are never downgraded to a restorable identifier.
  { kind: 'SECRET', regex: /(?<![\w-])eyJ[\w-]{5,}\.[\w-]{5,}\.[\w-]{5,}/g },
  { kind: 'SECRET', regex: /(?<![\w-])\d(?:[ -]?\d){14,18}(?![\w-])/g, accept: passesLuhn },
  { kind: 'EMAIL', regex: /(?<![\w.%+-])[\w.%+-]+@[a-z\d-]+(?:\.[a-z\d-]+)*\.[a-z]{2,}(?!\w)/gi },
  // +998 90 123 45 67 · 998901234567 · +998 (90) 123-45-67
  {
    kind: 'PHONE',
    regex: new RegExp(
      `(?<![\\w+-])(?:\\+\\s?)?998[\\s-]?\\(?${UZ_CODES}\\)?[\\s-]?\\d{3}[\\s-]?\\d{2}[\\s-]?\\d{2}(?![\\w-])`,
      'g'
    ),
  },
  // Any other international number written with a leading "+".
  {
    kind: 'PHONE',
    regex: /(?<![\w+-])\+\d{1,3}(?:[ -]?\(?\d{1,4}\)?){2,5}(?![\w-])/g,
    accept: hasPhoneLength,
  },
  // Local Uzbek form: 90 123 45 67 · (90) 123-45-67 · 901234567
  {
    kind: 'PHONE',
    regex: new RegExp(
      `(?<![\\w+-])\\(?${UZ_CODES}\\)?[\\s-]?\\d{3}[\\s-]?\\d{2}[\\s-]?\\d{2}(?![\\w-])`,
      'g'
    ),
  },
  // PINFL (personal identification number) — exactly 14 digits.
  { kind: 'ID', regex: /(?<![\w-])\d{14}(?![\w-])/g },
  // Passport: two capital letters + seven digits (AB1234567).
  { kind: 'ID', regex: /(?<![\w-])[A-Z]{2}\d{7}(?![\w-])/g },
];

const overlaps = (match: SensitiveMatch, taken: SensitiveMatch[]) =>
  taken.some((other) => match.start < other.end && other.start < match.end);

/** Non-overlapping matches in text order. Earlier patterns win on overlap. */
export const findSensitive = (text: string): SensitiveMatch[] => {
  const found: SensitiveMatch[] = [];

  PATTERNS.forEach(({ kind, regex, accept }) => {
    for (const match of text.matchAll(regex)) {
      const value = match[0];
      const start = match.index ?? 0;
      const candidate = { start, end: start + value.length, kind, value };
      if ((!accept || accept(value)) && !overlaps(candidate, found)) {
        found.push(candidate);
      }
    }
  });

  return found.sort((a, b) => a.start - b.start);
};

/** Replaces every match through `replace`, leaving the rest of the text intact. */
export const replaceSensitive = (
  text: string,
  replace: (match: SensitiveMatch) => string
): string => {
  const matches = findSensitive(text);
  if (!matches.length) return text;

  let result = '';
  let cursor = 0;
  matches.forEach((match) => {
    result += text.slice(cursor, match.start) + replace(match);
    cursor = match.end;
  });
  return result + text.slice(cursor);
};

export const toE164Phone = (value: string): string => {
  const digits = digitsOf(value);
  return digits.length === 9 ? `+998${digits}` : `+${digits}`;
};
